// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Collapse } from "react-bootstrap";

import { isSet } from "utils";
import { ActionType, IAction, IActionSortTable, raiseAction } from "actions";
import { makeSortHeader, tabClickHandler } from "guiutils";
import { IState } from "states/states";
import { IOcppCharger } from "states/ocppstates";
import { ITableSort } from "states/guistates";

import { ReactComponent as IconLocked } from "static/locked.svg";
import { ReactComponent as IconUnlocked } from "static/unlocked.svg";
import { ReactComponent as IconEdit } from "static/iconEdit.svg";
import { ReactComponent as IconExpandMore } from "static/iconExpandMore.svg";
import { ReactComponent as IconExpandLess } from "static/iconExpandLess.svg";

import ModalChargerSettings from "view/modal/ModalChargerSettings";

// sort key of table
const TABLE_NAME = "t_ocpp_chargers";

interface IChargersProps {
  ocppChargers: IOcppCharger[];
  canModifyChargers: boolean;
  tableSort: ITableSort | undefined;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curUser) => curUser.secId === state.site.user?.secId);
  const props: IChargersProps = {
    ocppChargers: state.ocpp.charger,
    canModifyChargers:
      (curUser?.accessRights.ocppSettings ?? false) ||
      (curUser?.accessRights.ocppChargerAdmin ?? false),
    tableSort: state.gui.tableSort[TABLE_NAME],
  };
  return props;
};

interface IChargersState {
  isExpanded: boolean;
}

interface IChargersDispatch {
  onSort: (sort: IActionSortTable) => void;
  onEdit: (id: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChargersDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
    onEdit: (id: number) => {
      dispatch(raiseAction(ActionType.ACT_EDIT_CHARGER, id));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  editHint: {
    id: "change settings",
  },
  lockedHint: {
    id: "charging is only allowed to valid RFID cards",
  },
  unlockedHint: {
    id: "charging is only allowed to everyone",
  },
});

class ChargersComponent extends React.Component<
  IChargersProps & IChargersDispatch & WrappedComponentProps,
  IChargersState
> {
  state: IChargersState = {
    isExpanded: true,
  };

  public render() {
    const sortCol = (this.props.tableSort?.column ?? "chargepoint") as keyof IOcppCharger;
    const chargerIndicis = Array.from(Array(this.props.ocppChargers.length).keys()).sort((a, b) => {
      const keyA = this.props.ocppChargers[a]?.[sortCol!];
      const keyB = this.props.ocppChargers[b]?.[sortCol!];
      const sort = isSet(keyA) && isSet(keyB) && keyA > keyB ? 1 : -1;
      return (this.props.tableSort?.isSortAscending !== false ? sort : -sort) as number;
    });
    const chargerItems = chargerIndicis.map((curIndex) => {
      return this.renderCharger(curIndex);
    });

    if (this.props.ocppChargers.length === 0) {
      return <></>;
    }

    return (
      <>
        <div id="ocpp-chargers">
          <h1
            className="link"
            onClick={() => {
              this.setState({ isExpanded: !this.state.isExpanded });
            }}
          >
            <Button variant="link" className="expand-button me-2 d-print-none">
              {this.state.isExpanded ? <IconExpandMore /> : <IconExpandLess />}
            </Button>
            <FormattedMessage id="Chargers" />
          </h1>
          <Collapse in={this.state.isExpanded}>
            <div>
              <table className="datatable fullwidthwhite">
                <thead>
                  <tr>
                    {this.makeSortHeader("chargepoint", <FormattedMessage id="Chargepoint-ID" />)}
                    {this.makeSortHeader("name", <FormattedMessage id="Name" />, ["optional2"])}
                    {this.makeSortHeader("vendor", <FormattedMessage id="Vendor" />, ["optional2"])}
                    {this.makeSortHeader("model", <FormattedMessage id="Model" />, ["optional"])}
                    {this.makeSortHeader("firmware", <FormattedMessage id="Software" />, [
                      "optional",
                    ])}
                    {this.makeSortHeader("serial", <FormattedMessage id="Serial Number" />)}
                    {this.makeSortHeader(
                      "numberofconnectors",
                      <FormattedMessage id="Connectors" />,
                      ["optional"],
                    )}
                    {this.makeSortHeader("restrictAuth", <FormattedMessage id="Authentication" />)}
                    <th></th>
                  </tr>
                </thead>
                <tbody>{chargerItems}</tbody>
              </table>
            </div>
          </Collapse>
        </div>
        <ModalChargerSettings />
      </>
    );
  }

  // this function is just a short-cut to reduce boilerplate
  private makeSortHeader(
    column: keyof IOcppCharger,
    caption: JSX.Element,
    classes?: string[],
    title?: string,
  ) {
    return makeSortHeader(
      TABLE_NAME,
      column,
      caption,
      this.props.tableSort ?? { column: "chargepoint", isSortAscending: true },
      this.props.onSort,
      classes,
      title,
    );
  }

  private renderCharger(index: number) {
    const charger = this.props.ocppChargers[index];

    return (
      <tr key={`${charger.fk_charger}${charger.chargepoint}`}>
        <td>{charger.chargepoint}</td>
        <td className="optional2">{charger.name}</td>
        <td className="optional2">{charger.vendor}</td>
        <td className="optional">{charger.model}</td>
        <td className="optional">{charger.firmware}</td>
        <td className="nowrap">{charger.serial}</td>
        <td className="optional">{charger.numberofconnectors}</td>
        <td className="nowrap">
          {charger.restrictAuth ? (
            <>
              <IconLocked className="me-1" />
              <FormattedMessage id="Authorized" />
            </>
          ) : (
            <>
              <IconUnlocked className="me-1" />
              <FormattedMessage id="Public" />
            </>
          )}
        </td>
        {this.props.canModifyChargers && (
          <td className="edit-col img-link nowrap">
            <span
              tabIndex={0}
              onKeyDown={tabClickHandler}
              onClick={() => this.props.onEdit(charger.fk_charger)}
              title={this.props.intl.formatMessage(messages.editHint)}
            >
              <IconEdit />
            </span>
          </td>
        )}
      </tr>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ChargersComponent));
